import { useMemo } from "react";
import { format } from "date-fns";
import { Avatar, Badge, Group, Indicator, Stack, Text, UnstyledButton } from "@mantine/core";
import type { AssociatedObject } from "./types";
import { getEmailViewModel, humanizeEmailTag } from "./emailUtils";

type EmailListItemProps = {
  email: AssociatedObject;
  onOpen: (email: AssociatedObject) => void;
};

const EmailListItem = ({ email, onOpen }: EmailListItemProps) => {
  const view = useMemo(() => getEmailViewModel(email), [email]);

  const formattedDate = useMemo(() => {
    if (!view.timestamp) return "";
    const date = new Date(view.timestamp);
    if (Number.isNaN(date.getTime())) return "";
    return format(date, "d MMM");
  }, [view.timestamp]);

  const avatarLabel = (view.sender.name || view.sender.email || view.sender.display || "?")
    .slice(0, 1)
    .toUpperCase();

  const recipientPreview = view.recipients.length
    ? `${view.recipients.slice(0, 2).join(", ")}${view.recipients.length > 2 ? ` +${view.recipients.length - 2}` : ""}`
    : "";

  const isUnread = view.labels.includes("UNREAD");
  const rightTag = isUnread
    ? "UNREAD"
    : view.direction || view.labels.find((label) => label === "IMPORTANT");

  return (
    <UnstyledButton
      onClick={() => onOpen(email)}
      className="w-full"
    >
      <Group
        gap="sm"
        wrap="nowrap"
        className="px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <Indicator disabled={!isUnread} color="main.5" size={8} offset={2} withBorder>
          <Avatar radius="xl" size={36} variant="light" color="gray">
            {avatarLabel}
          </Avatar>
        </Indicator>

        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
            <Text fw={600} c="text.9" size="sm" lineClamp={1} style={{ flex: 1 }}>
              {view.subject}
            </Text>
            {formattedDate && (
              <Text size="xs" c="text.5" style={{ whiteSpace: "nowrap" }}>
                {formattedDate}
              </Text>
            )}
          </Group>

          <Text size="xs" c="text.5" lineClamp={1}>
            {view.sender.display}
            {recipientPreview ? ` • ${recipientPreview}` : ""}
          </Text>

          {view.snippet && (
            <Text size="sm" c="text.6" lineClamp={1}>
              {view.snippet}
            </Text>
          )}
        </Stack>

        {rightTag && (
          <Badge
            variant={rightTag === "UNREAD" ? "filled" : "light"}
            color={rightTag === "UNREAD" ? "main" : "main"}
            radius="md"
            size="sm"
            title={rightTag}
          >
            {humanizeEmailTag(rightTag)}
          </Badge>
        )}
      </Group>
    </UnstyledButton>
  );
};

export default EmailListItem;
